import _extends from "@babel/runtime/helpers/esm/extends";
import {
	Camera,
	ReactThreeFiber,
	useFrame,
	useThree,
} from "@react-three/fiber";
import * as React from "react";
import { OrbitControls as OrbitControls$1 } from "three-stdlib";
import * as THREE from "three";

import TWEEN from "@tweenjs/tween.js";
import animateCamera from "@utils/animateCamera";
import { OrbitControlsChangeEvent } from "@react-three/drei";
import { useStore } from "@state/store";
import { SceneData } from "@data/scene";
import { useLocation } from "react-router-dom";

export type OrbitControlsProps = Omit<
	ReactThreeFiber.Overwrite<
		ReactThreeFiber.Object3DNode<OrbitControls$1, typeof OrbitControls$1>,
		{
			camera?: Camera;
			domElement?: HTMLElement;
			enableDamping?: boolean;
			enableZoom?: boolean;
			makeDefault?: boolean;
			onChange?: (e?: OrbitControlsChangeEvent) => void;
			onEnd?: (e?: OrbitControlsChangeEvent) => void;
			onStart?: (e?: OrbitControlsChangeEvent) => void;
			regress?: boolean;
			target?: ReactThreeFiber.Vector3;
			keyEvents?: boolean | HTMLElement;
		}
	>,
	"ref"
>;



const OrbitControlsCustom = /*#__PURE__*/ React.forwardRef(
	(
		{
			makeDefault,
			camera,
			regress,
			domElement,
			enableDamping = true,
			enableZoom = false,
			onChange,
			onStart,
			onEnd,
			...restProps
		}: OrbitControlsProps,
		ref,
	) => {
		const invalidate = useThree((state) => state.invalidate);
		const defaultCamera = useThree((state) => state.camera);
		const gl = useThree((state) => state.gl);
		const events = useThree((state) => state.events);
		const set = useThree((state) => state.set);
		const get = useThree((state) => state.get);
		const performance = useThree((state) => state.performance);
		const explCamera = camera || defaultCamera;
		const explDomElement = domElement || events.connected || gl.domElement;
		const controls = React.useMemo(
			() => new OrbitControls$1(explCamera),
			[explCamera],
		);
		const currentApt = useStore((s) => s.currentApt);
		const currentAmenityIndex = useStore((s) => s.currentAmenityIndex);
		const activeLevel = useStore((s) => s.activeLevel);
		const amenityModelVisible = useStore((s) => s.amenityModelVisible);
		const location = useLocation();
		const currentSceneData = React.useMemo(() => {
			const data = SceneData &&
				SceneData.find((data) => {
					return data.page === location.pathname;
				});
			return data;
		}, [currentApt, location, amenityModelVisible, currentAmenityIndex]);

		const currentViewport = React.useMemo(() => {
			if (!currentSceneData) {
				return null;
			}

			if (location.pathname === "/surf") {
				return currentSceneData.rest;
			}

			if (location.pathname === "/residences") {
				if (currentApt && currentApt.unit) {
					return currentSceneData.aspects.find((data: any) =>
						currentApt.aspect.includes(data.aspect)
							? currentApt.aspect[0]
							: null
					);
				} else return currentSceneData.rest;
			}

			if (location.pathname === "/amenities") {
				if (!amenityModelVisible) {
					if (activeLevel === null) {
						return currentSceneData.rest;
					}

					if (activeLevel !== null) {
						return currentSceneData.notZoomed[activeLevel as number];
					}
				}

				if (amenityModelVisible) {
					if (currentAmenityIndex !== null) {
						const dir = new THREE.Vector3();
						const newPosition = new THREE.Vector3();

						dir.subVectors(defaultCamera.position, controls.target).normalize();

						const scale =
							currentSceneData.zoomed[currentAmenityIndex].zoomFactor;

						newPosition.addScaledVector(dir, scale);

						return {
							...currentSceneData.zoomed[currentAmenityIndex],
							camera: [newPosition.x, newPosition.y, newPosition.z],
						};
					}

					if (currentAmenityIndex === null) {
						return currentSceneData.rest;
					}
				}
			}
		}, [
			currentSceneData,
			currentApt,
			currentAmenityIndex,
			location,
			amenityModelVisible,
		]);

		const cameraSettings = useStore((state) => state.cameraSettings);

		useFrame(({ camera }) => {
			// console.log([camera.position.x,camera.position.y,camera.position.z])
			TWEEN.update();
			controls.update();
		}, -1);

		React.useEffect(() => {
			controls.connect(explDomElement);
			return () => void controls.dispose();
		}, [explDomElement, regress, controls, invalidate]);

		React.useEffect(() => {
			const callback = (e) => {
				invalidate();
				if (regress) performance.regress();
				if (onChange) onChange(e);
			};

			controls.addEventListener("change", callback);
			if (onStart) controls.addEventListener("start", onStart);
			if (onEnd) controls.addEventListener("end", onEnd);
			return () => {
				if (onStart) controls.removeEventListener("start", onStart);
				if (onEnd) controls.removeEventListener("end", onEnd);
				controls.removeEventListener("change", callback);
			};
		}, [onChange, onStart, onEnd, controls, invalidate, regress, performance]);

		React.useEffect(() => {
			if (makeDefault) {
				const old = get().controls;
				set({
					controls,
				});
				return () =>
					set({
						controls: old,
					});
			}
		}, [makeDefault, controls, get, set]);

		const animateToLookAt = React.useCallback(
			(destination, factor = 1.0) => {
				const { camera: position, target, fov } = destination;
				if (position && target && controls && fov) {
					const [cx, cy, cz] = position;
					const [tx, ty, tz] = target;

					animateCamera(explCamera, controls, {
						position: {
							x: cx,
							y: cy,
							z: cz,
						},
						target: {
							x: tx,
							y: ty,
							z: tz,
						},
						duration: 900,
					});
				}
			},
			[controls, explCamera],
		);

		const [delay] = React.useState(50);

		React.useEffect(() => {
			if (currentViewport) {
				let cameraSettings = {
					camera: currentViewport?.camera,
					target: currentViewport?.target,
					fov: currentViewport?.fov,
					enabled: true,
					delay: 0,
				};
				setTimeout(() => {
					animateToLookAt(cameraSettings);
				}, delay);
			} else {
				let cameraSettings = {
					camera: [-5.994564858075958, 0.6976831814830297, -2.8280573179870045],
					target: [0, 0, 0],
					fov: 55,
					enabled: true,
					delay: 0,
				};
				setTimeout(() => {
					animateToLookAt(cameraSettings);
				}, delay);
			}
		}, [animateToLookAt, cameraSettings, delay, currentViewport]);

		return /*#__PURE__*/ React.createElement(
			"primitive",
			_extends(
				{
					ref: ref,
					object: controls,
					enableDamping: enableDamping,
					enableZoom: false,
				},
				{
					...restProps,
					minPan: new THREE.Vector3(0, 0, 0),
					maxPan: new THREE.Vector3(0, 0, 0),
					minDistance: 0.25,
					maxDistance: 5,
					maxPolarAngle: 1.65,
					minPolarAngle: 0,
				},
			),
		);
	},
);

export default OrbitControlsCustom;
